import { Route, Routes, useLocation } from 'react-router';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import Onboarding from './pages/Onboarding';
import ForgotPasswordPage from './pages/ForgotPassword';
import SetNewPasswordPage from './pages/SetNewPassword';
import PricingPlansPage from './pages/PricingPlans';
import DashboardPage from './pages/Dashboard';
import ReportIncidentPage from './pages/ReportIncident';
import IncidentReportsDraftsPage from './pages/IncidentReportsDrafts';
import IncidentReportingPage from './pages/IncidentReporting';
import MyReportedIncidentsPage from './pages/MyReportedIncidents';
import ReportedIncidentsPage from './pages/ReportedIncidents';
import CorrectiveActionsAssignedToMePage from './pages/CorrectiveActionsAssignedToMe';
import AllCorrectiveActionDetailPage from './pages/AllCorrectiveActionDetail';
import IncidentReviewPage from './pages/IncidentReview';
import IncidentReviewDetailPage from './pages/IncidentReviewDetail';
import AssignedCorrectiveActionsPage from './pages/AssignedCorrectiveActionsPage';
import UserManagementPage from './pages/UserManagement';
import EmployeeOnboardingPage from './pages/EmployeeOnboarding';
import DepartmentsPage from './pages/Departments';
import PaymentSuccessPage from './views/PaymentSuccess';
import Verify from './views/Verify';
import { useEffect, useState } from 'react';
import { getSession, logout } from './actions/useAction';
import { useDispatch } from 'react-redux';
import { AppDispatch } from './store/store';
import CheckValidity from './CheckValidity';
import { ToastContainer } from 'react-toastify';
import { useLocalStorage } from '@uidotdev/usehooks';
import { LoginResponseData } from '@EHSNavigator/backend';
import moment from 'moment';
import NotFound from './components/NotFound';
import PermissionRoute from './PermissionRoute';
import { AuthApi } from './client/api';
import AssignedCorrectiveActionsDetailPage from './pages/AssignedCorrectiveActionsDetail';
import AllCorrectiveActionHubPage from './pages/AllCorrectiveActionHubPage';
import CorrectiveActionsAssignedToMeDetailPage from './pages/CorrectiveActionsAssignedToMeDetail';
import IncidentReviewDraftsPage from './pages/IncidentReviewDrafts';
import StatisticsPage from './pages/Statistics';
import CustomIncidentTypesPage from './pages/CustomIncidentTypes';
import FAQsPage from './pages/FAQs';
import ContactSupportPage from './pages/ContactSupport';
import UserGuidePage from './pages/UserGuide';
import GeneralSettingsPage from './pages/GeneralSettings';
import BillingSuccesPage from './pages/BillingSucces';
import BillingCancelPage from './pages/BillingCancel';
import PaymentCancelPage from './views/PaymentCancel';
import BulkUserCreationPage from './pages/BulkUserCreation';
import TaskReviewPage from './pages/TaskReview';
import CustomQuestionsPage from './pages/CustomQuestions';
import InjuriesHubPage from './pages/InjuriesHub';
import InvestigationReportingPage from './pages/InvestigationReporting';
import InjuriesDetailsPage from './pages/InjuriesDetails';
import DocumentLibraryPage from './pages/DocumentLibrary';
import OshaReportingPage from './pages/OshaReporting';
import CreateOshaReportPage from './pages/CreateOshaReport';
import OshaReportingLogs from './pages/OshaReportingLogs';
import ResubmitToOshaReportPage from './pages/ResubmitToOshaReport';
import DeleteAccountPage from './views/DeleteAccount';
import AdminDashboardPage from './pages/AdminDashboard';
import AdminRoute from './AdminRoute';
import PlatformOwnerRoute from './PlatformOwnerRoute';
import OnboardOrganizationPage from './pages/OnboardOrganization';
import OnboardOrganizationDetailsPage from './pages/OnboardOrganizationDetails';
import OrganizationRequestAccessPage from './pages/OrganizationRequestAccess';
import OnboardPaymentPage from './pages/OnboardPayment';

function App() {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const [isLoggedOut, setIsLoggedOut] = useState(false);
  const [userInfo, setUserInfo] = useLocalStorage<LoginResponseData | null>(
    'userInfo',
    null,
  );

  useEffect(() => {
    dispatch(getSession(userInfo));
  }, [userInfo]);

  useEffect(() => {
    if (!userInfo || !userInfo.exp?.refreshToken) return;

    const checkAccessTokenExpiry = async () => {
      const now = moment();
      const accessTokenExp = moment(userInfo?.exp?.accessToken);

      if (accessTokenExp.diff(now, 'seconds') <= 10) {
        const {
          data: { valid },
        } = await AuthApi.authControllerCheckSessionValidity({
          refreshTokenDTO: {
            refresh: String(userInfo?.refresh),
          },
        });
        if (valid) {
          const res = await AuthApi.authControllerRefreshToken({
            refreshTokenDTO: { refresh: String(userInfo?.refresh) },
          });
          const { accessToken, accessTokenExpiration } = res.data;
          setUserInfo({
            ...userInfo,
            accessToken,
            exp: {
              ...userInfo.exp,
              accessToken: accessTokenExpiration,
            },
          });
        }
      }
    };

    const checkTokenExpiry = () => {

      const refreshTokenExp = moment(userInfo.exp.refreshToken);
      const now = moment();

      if (refreshTokenExp.diff(now, 'seconds') <= 10 && !isLoggedOut) {
        setIsLoggedOut(true);
        dispatch(logout(setUserInfo));
      }
    };

    const interval = setInterval(() => {
      checkAccessTokenExpiry();
      checkTokenExpiry();
    }, 10000);

    checkAccessTokenExpiry();
    checkTokenExpiry();

    return () => clearInterval(interval);
  }, [userInfo, isLoggedOut]);

  useEffect(() => {
    if (isLoggedOut) {
      setTimeout(() => {
        setIsLoggedOut(false);
      }, 5000);
    }
  }, [isLoggedOut]);

  if (import.meta.env.MODE === 'production' && !location?.pathname?.includes('documentation')) {
    const script = document.createElement('script');
    script.innerHTML = `
      window.Userback = window.Userback || {};
      Userback.access_token = "A-iIJRcr82HrLpM0S1JEk3Op3Xa";
      Userback.user_data = { id: "PROJ1261" };
      (function(d) {
        var s = d.createElement('script');
        s.async = true;
        s.src = 'https://static.userback.io/widget/v1.js';
        (d.head || d.body).appendChild(s);
      })(document);
    `;
    document.body.appendChild(script);
  }

  return (
    <>
      <ToastContainer />

      <Routes>
        <Route element={<PublicRoute />}>
          <Route index element={<Onboarding />} />
          <Route path="sign-up" element={<SignUp />} />
          <Route path="sign-in" element={<SignIn />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route
            path="set-password"
            element={
              <CheckValidity>
                <SetNewPasswordPage />
              </CheckValidity>
            }
          />
          <Route
            path="pricing-plans"
            element={
              <CheckValidity validation="user">
                <PricingPlansPage />
              </CheckValidity>
            }
          />
          <Route
            path="payment/success"
            element={
              <CheckValidity validation="trial">
                <PaymentSuccessPage />
              </CheckValidity>
            }
          />
          <Route
            path="payment/cancel"
            element={
              <CheckValidity validation="trial">
                <PaymentCancelPage />
              </CheckValidity>
            }
          />
          <Route
            path="verify"
            element={
              <CheckValidity>
                <Verify />
              </CheckValidity>
            }
          />
          <Route
            path="onboard-payment"
            element={
              <CheckValidity validation="uid">
                <OnboardPaymentPage />
              </CheckValidity>
            }
          />
        </Route>
        <Route element={<PrivateRoute />}>
          <Route path="delete-account" element={
            <CheckValidity validation="deleteAccount">
              <DeleteAccountPage />
            </CheckValidity>
          } />
          <Route path="billing/success" element={<BillingSuccesPage />} />
          <Route path="billing/cancel" element={<BillingCancelPage />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="report-incident">
            <Route index element={<ReportIncidentPage />} />
            <Route path="drafts" element={<IncidentReportsDraftsPage />} />
            <Route path="report" element={<IncidentReportingPage />} />
          </Route>
          <Route path="my-reported-incidents">
            <Route index element={<MyReportedIncidentsPage />} />
            <Route
              path=":my-reported-incidents"
              element={<ReportedIncidentsPage />}
            />
          </Route>

          <Route path="assigned-to-me">
            <Route index element={<CorrectiveActionsAssignedToMePage />} />
            <Route
              path=":assigned-to-me-id"
              element={<CorrectiveActionsAssignedToMeDetailPage />}
            />
          </Route>
          <Route path="user-guides" element={<UserGuidePage />} />
          <Route path="faqs" element={<FAQsPage />} />
          <Route path="contact-support" element={<ContactSupportPage />} />
          <Route path="general-settings" element={<GeneralSettingsPage />} />

          {/* Admin Routes */}
          <Route element={<AdminRoute />}>
            <Route path="admin/customers" element={<AdminDashboardPage />} />
          </Route>

          {/* Platform Owner Routes */}
          <Route element={<PlatformOwnerRoute />}>
            <Route path="admin/onboard-organization">
              <Route index element={<OnboardOrganizationDetailsPage />} />
              <Route path="form" element={<OnboardOrganizationPage />} />
            </Route>
            <Route path="admin/organization-requests" element={<OrganizationRequestAccessPage />} />
          </Route>

          {/* Permission Based Routes */}
          <Route element={<PermissionRoute />}>
            <Route path="statistics" element={<StatisticsPage />} />
            <Route path="incident-review">
              <Route index element={<IncidentReviewPage />} />
              <Route
                path=":incident-review-id"
                element={<IncidentReviewDetailPage />}
              />
              <Route path="drafts" element={<IncidentReviewDraftsPage />} />
            </Route>
            <Route path="assigned-corrective-actions">
              <Route index element={<AssignedCorrectiveActionsPage />} />
              <Route
                path=":assigned-corrective-actions-id"
                element={<AssignedCorrectiveActionsDetailPage />}
              />
            </Route>
            <Route path="all-corrective-actions">
              <Route index element={<AllCorrectiveActionHubPage />} />
              <Route
                path=":all-corrective-actions-id"
                element={<AllCorrectiveActionDetailPage />}
              />
            </Route>
            <Route
              path="custom-categories"
              element={<CustomIncidentTypesPage />}
            />
            <Route path="user-roles" element={<UserManagementPage />} />
            <Route
              path="employee-onboarding"
              element={<EmployeeOnboardingPage />}
            />
            <Route path="departments" element={<DepartmentsPage />} />
            <Route
              path="bulk-user-creation"
              element={<BulkUserCreationPage />}
            />
            <Route path="task-review" element={<TaskReviewPage />} />

            <Route path="custom-questions" element={<CustomQuestionsPage />} />

            <Route path="injuries">
              <Route index element={<InjuriesHubPage />} />
              <Route
                path="create-investigation/:id"
                element={<InvestigationReportingPage />}
              />
              <Route
                path=":injuries-view-by-id"
                element={<InjuriesDetailsPage />}
              />
            </Route>
            <Route path="osha-reporting">
              <Route index element={<OshaReportingPage />} />
              <Route path="report" element={<CreateOshaReportPage />} />
              <Route path="logs" element={<OshaReportingLogs />} />
              <Route path="resubmit" element={<ResubmitToOshaReportPage />} />
            </Route>
          </Route>
          <Route path="documents" element={<DocumentLibraryPage />} />
        </Route>
        <Route path="404" element={<NotFound />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

export default App;
